import { safeJsonParse } from "@swiggy-private/js-utils";
import type { AnalyticsEventPayload } from "@swiggy-private/analytics/dist/interfaces";
import {
    DigitalMenuVersion,
    IDigitalMenuConfigData,
    isApp,
    PinGenerationStatus,
    ScreenNames,
    safeStringify,
} from "@swiggy-private/portal-dineout-context";
import type { IReducerResponse, ITableContext } from "@swiggy-private/portal-dineout-context";
import { OBJECT_VALUES } from "../constants/analytics";
import { CustomWidgetTypes, UserCategory, WidgetTypes } from "../Menu/types";

const FE_HANDLED_ANALYTICS_CONTEXT: string[] = [...Object.values(CustomWidgetTypes)];

const CART_WIDGETS: string[] = [WidgetTypes.CART_ITEM_LIST, WidgetTypes.CTA_LIST];

const PIN_VERIFICATION_FLOW_WIDGETS: string[] = [
    WidgetTypes.ITEM_CATEGORY_TYPE,
    WidgetTypes.NESTED_ITEM_CATEGORY_TYPE,
    WidgetTypes.FAB_WIDGET,
];

export const isCurrentUserHost = (pinStatus: string): boolean => {
    return (
        pinStatus === PinGenerationStatus.PIN_ALREADY_GENERATED ||
        pinStatus === PinGenerationStatus.SUCCESS ||
        pinStatus === PinGenerationStatus.USER_VERIFIED
    );
};

export const isPinVerificationFlowWidget = (source?: string) => {
    return !source ? false : PIN_VERIFICATION_FLOW_WIDGETS.includes(source);
};

export const shouldShowPinHalfCard = (pinStatus?: string, userStatus?: string): boolean => {
    if (!pinStatus) return true;
    return (
        pinStatus === PinGenerationStatus.PIN_GENERATED_BY_OTHER_USER &&
        userStatus !== PinGenerationStatus.USER_VERIFIED
    );
};

const enrichContext = (data: Record<string, string | number>, context = "") => {
    if (!context || context === "-") return context;
    Object.entries(data).forEach(([key, value]) => {
        context = context.replaceAll(`{${key}}`, value?.toString() || "");
    });
    return context;
};

export const isV2Config = (config: IDigitalMenuConfigData | null) => {
    return config?.version === DigitalMenuVersion.V2;
};

export const getV2ConfigFlags = (config: IDigitalMenuConfigData | null) => {
    return {
        isV2: isV2Config(config),
        isV2InApp: isApp() && isV2Config(config),
    };
};

export const updateAnalyticsData = ({
    analyticsData,
    tableContext,
    screenName,
    state,
    config,
}: {
    analyticsData?: Partial<AnalyticsEventPayload>;
    tableContext: ITableContext;
    screenName: ScreenNames;
    state: IReducerResponse;
    config: IDigitalMenuConfigData | null;
    eventType?: "impression" | "click" | undefined;
}) => {
    const { isV2, isV2InApp } = getV2ConfigFlags(config);
    const theme = isV2InApp ? config?.themeInfo?.theme : undefined;
    const requestId = state.menu.requestId;
    const { tableNo, pin, status } = state.pin.data;
    const { tableId, restaurantId, parentOrderId } = tableContext;
    const isUserHost = isCurrentUserHost(status);
    const userCategory = isUserHost ? UserCategory.PRIMARY : UserCategory.SECONDARY;
    const isFEHandledAnalyticsContext = FE_HANDLED_ANALYTICS_CONTEXT.includes(
        analyticsData?.source || ""
    );

    const isCartWidget = CART_WIDGETS.includes(analyticsData?.source || "");

    const defaultContext = {
        table_no: tableNo,
        table_id: tableId,
        pin,
        "user-category": userCategory,
        parentOrderId,
        is_v2_config: isV2,
        theme,
    };

    const contextData = { page: screenName, pin, user_category: userCategory };

    const defaultExtraParams = {
        request_id: requestId,
        restaurant_id: restaurantId,
    };

    if (isCartWidget) {
        delete defaultExtraParams.request_id;
    }

    let finalAnalyticsData = {
        ...analyticsData,
        category: analyticsData?.category || screenName,
        context: safeStringify({
            ...(isFEHandledAnalyticsContext
                ? safeJsonParse(analyticsData?.context || "{}", {})
                : safeJsonParse(
                      enrichContext(contextData, analyticsData?.context || "-") || "{}",
                      {}
                  )),
            ...defaultContext,
        }),
        extraParams: safeStringify({
            ...safeJsonParse(analyticsData?.extraParams || "{}", {}),
            ...defaultExtraParams,
        }),
    };

    if (
        isPinVerificationFlowWidget(analyticsData?.source) &&
        shouldShowPinHalfCard(state?.pin?.data?.status ?? "", state?.pin?.data?.userStatus ?? "") &&
        analyticsData?.label === OBJECT_VALUES.ADD_ITEM
    ) {
        finalAnalyticsData = { ...finalAnalyticsData, label: OBJECT_VALUES.ADD_PIN };
    }

    return finalAnalyticsData;
};
